import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, timeConverterSM} from '../../../Utils';
import Axios from 'axios';
import Spinner from '../../common/generic/Spinner';
import { resourceDomain } from '../../../ApiHelper';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;
    const [tallyJS, setTallyJS] = useState({});
    const [masterLevel, setMasterLevel] = useState('80');
    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getProgramMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getProgramMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getProgramMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, class:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }

        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getProgramMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.id.toLowerCase(),
                    func:'getProgramMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    const fetchTallyJS = async () => {
        if(selectedProgram.id==='') return;
        const response = await Axios.get(`${resourceDomain}programs/${selectedProgram.id.toLowerCase()}/data/progstruct/parenttally.js`);
        setTallyJS({...response});
    }; 

    // convert database['getProgramMarks'] into setMarkRecords
    // UDATES - when database['getProgramMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getProgramMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getProgramMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getProgramMarks'].isLoaded,
            data: database['getProgramMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});
        fetchTallyJS();
    }, [database['getProgramMarks']]); 

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;      
        if(isObjectEmpty(tallyJS)) return;

        let reportData = JSON.parse(localStorage.getItem('reportData'));

        if(masterLevel !== reportData.masterLevel)
            setMasterLevel(reportData.masterLevel);

        const tally = tallyJS.data.split('\n').reduce((acc,tallyRec)=>{
            if(tallyRec.indexOf('+ROOT') > -1){
                acc+= parseInt(tallyRec.split('=')[1]);
            }
            return acc;
        },0);

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                let studentMarkRecords = markRecords[studentID];
                if(!studentMarkRecords) return acc; // still loading marks

                let combinedMarks = modulesData.reduce((macc,moduleRec)=>{
                    let moduleID = moduleRec.moduleID;
                    let modMark = moduleRec.links.reduce((lacc,linkRec)=>
                    {                   
                        let linkID= linkRec.linkID;
                        if(studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`]) {
                            let mark = studentMarkRecords.data[`${studentID}+${moduleID[0]}+${linkID}`];
                            lacc.LastUsed = (parseInt(mark.LastUsed) > lacc.LastUsed)?parseInt(mark.LastUsed):lacc.LastUsed;
                            lacc.TimesCompleted += (parseInt(mark.TimesCompleted) > 0)?1:0;
                            lacc.QuestionsRight += parseInt(mark.QuestionsRight);
                            lacc.QuestionsAttempted += parseInt(mark.QuestionsAttempted);
                            lacc.CumulativeTime += parseInt(mark.CumulativeTime);
                        } 
                        return lacc;
                    },{LastUsed:0, TimesCompleted:0, QuestionsRight:0, QuestionsAttempted:0, CumulativeTime:0});

                    if(parseInt(modMark.LastUsed) > macc.LastUsed){
                        macc.LastUsed = parseInt(modMark.LastUsed);
                        macc.currentModule = moduleID[1];
                    }
                    macc.TimesCompleted += modMark.TimesCompleted;
                    macc.QuestionsRight += modMark.QuestionsRight;
                    macc.QuestionsAttempted += modMark.QuestionsAttempted;
                    macc.CumulativeTime += modMark.CumulativeTime;
                    return macc;

                },{LastUsed:0, currentModule:'', TimesCompleted:0, QuestionsRight:0, QuestionsAttempted:0, CumulativeTime:0});

                combinedMarks.totalActs = tally;
                acc[`${studentRec.id}+${studentRec.name}`] = {student: studentRec, mark: combinedMarks};
                return acc;
            },{});
            setProcessedMarks(studentsModuleMarks);

            if(csvData){
                let newLine = '%0A';
                let csvString = '';
                csvString =`"* The mastery level for a given class can be set by editing the class under the Manage Classes tab. An activity is considered to be mastered when a student answers at least ${masterLevel}% of questions correctly on a given attempt."${newLine}`;
                csvString +=`"Students without marks will not appear in this report"${newLine}`;
                let headerStr = `"${selectedProgram.name}","Mastery Report"${newLine}`;
                let tableHeaderStr = '"Student","Activities Mastered","Questions Correct",Questions Attempted","Time in Program","Current Module"'+newLine;
                csvString += headerStr + tableHeaderStr;

                Object.keys(studentsModuleMarks).forEach((key) => { 
                    const { student, mark} = studentsModuleMarks[key];
                    const { TimesCompleted, QuestionsRight, QuestionsAttempted, CumulativeTime, currentModule} = mark;
                    let recordStr = `"${student.name}","${TimesCompleted}/${tally}","${QuestionsRight}","${QuestionsAttempted}","${toTime(CumulativeTime)} ${toTimeType(CumulativeTime)}","${currentModule}"${newLine}`;
                    csvString += recordStr;
                });

                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, class:false});
                    return;
                }
                if(Object.keys(studentsModuleMarks).length==1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'CompareReport_'+selectedProgram.name+'_'+today,csvString);
                //uploadCSV('compareReport',csvString);
                csvData.setloadingCSV({...csvData.loadingCSV, class:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);
    useEffect(() =>{tryLoadingNow();},[tallyJS]);  

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;

    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
        <Col style={{textAlign: 'left'}} xs='2'> Student </Col>
        <Col style={{textAlign: 'left'}} xs='2'> Activities Mastered </Col>
        <Col style={{textAlign: 'center'}} xs='2'>Questions Correct</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Questions Attempted</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Time in Program</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Current Module</Col>
        {/* <Col style={{textAlign: 'center'}} xs='2'>Current Module</Col> */}
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'></Col> 
            <Col xs='4'>{selectedProgram.name} </Col>
            <Col xs='4'>Mastery Report</Col>
        </Row></h4>
    </center>;

    const Footer = ()=> <center><hr/>* The mastery level for a given class can be set by editing the class under the Manage Classes tab. An activity is considered to be mastered when a student answers at least {masterLevel}% of the questions correctly on a given attempt.</center>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {headerComp}
        {titleRow}
        <hr/>
        {Object.keys(processedMarks).map((key, i) => ( 
            <ClassReportComp key={i} header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key]}/>            
        ))}
        <Footer/>
    </>;
};

const ClassReportComp = ({header, tableData}) => {

    return (
        <>
            <TableRow data={tableData} fontWeight={'normal'}/>
        </>
    );
};
ClassReportComp.propTypes = {
    tableData: PropTypes.object
};

const TableRow = ({data, fontWeight}) => {
    const { student, mark} = data;
    const { TimesCompleted, QuestionsRight, QuestionsAttempted, CumulativeTime, totalActs, currentModule} = mark;

    return (
        <Row style={{fontSize:'0.9em'}}>
            <Col style={{textAlign: 'left', fontWeight}} xs='2'>{student.name}</Col>
            <Col style={{textAlign: 'center', fontWeight}} xs='2'>{TimesCompleted} / {totalActs}</Col>
            <Col style={{textAlign: 'center', fontWeight}} xs='2'>{QuestionsRight}</Col>
            <Col style={{textAlign: 'center', fontWeight}} xs='2'>{QuestionsAttempted}</Col>
            <Col style={{textAlign: 'center', fontWeight}} xs='2'>{toTime(CumulativeTime)} {toTimeType(CumulativeTime)}</Col>
            <Col style={{textAlign: 'center', fontWeight}} xs='2'>{currentModule}</Col>
        </Row>
    );
};

TableRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;